import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import contactUs from "../assets/img/contact-us.png";
import { useTranslation } from "react-i18next";
import { useSettings } from "../context/SettingsContext";
import api from "../api";

function Contact() {
  const { t, i18n } = useTranslation();
  const settings = useSettings();
  const isBn = i18n.language === "bn";

  const getEmailLink = (emailStr) => {
    if (!emailStr) return "";
    return emailStr.replace(/^(email|ইমেল)\s*:\s*/i, "").trim();
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setSubmitting(true);
    try {
      const res = await api.post("/contact", {
        name,
        email,
        subject,
        message
      });
      if (res.data && res.data.success) {
        setStatus({ success: true, message: t("contact.formSuccess", "Your message has been sent successfully!") });
        setName("");
        setEmail("");
        setSubject("");
        setMessage("");
      } else {
        setStatus({ success: false, message: res.data?.message || t("contact.formFail", "Failed to send message. Please try again.") });
      }
    } catch (err) {
      setStatus({ success: false, message: err?.response?.data?.message || t("contact.formFail", "Failed to send message. Please try again.") });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-gray-50">
      <Helmet>
        <title>Contact Us | Palli Market - We're Here to Help</title>
        <meta name="description" content="Get in touch with Palli Market. Contact us by email, phone, or visit our location. We're happy to help with your orders, returns, or any queries." />
        <link rel="canonical" href="https://pallimarket.bd/contact-us" />
        <meta property="og:title" content="Contact Us | Palli Market" />
        <meta property="og:description" content="Get in touch with Palli Market. Contact us by email, phone, or visit our location. We're happy to help with your orders, returns, or any queries." />
        <meta property="og:url" content="https://pallimarket.bd/contact-us" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="flex justify-center py-10 lg:py-20 bg-page-header-bg bg-indigo-100 w-full bg-cover bg-no-repeat bg-bottom bg-page-header ">
        <div className="flex mx-auto w-full max-w-screen-2xl px-3 sm:px-10">
          <div className="w-full flex justify-center flex-col relative">
            <h2 className="text-xl md:text-3xl lg:text-4xl font-bold text-center text-black">
              {t("contact.title", "Contact Us")}
            </h2>
          </div>
        </div>
      </div>
      <div className="bg-white">
        <div className="max-w-screen-2xl mx-auto lg:py-20 py-10 px-4 sm:px-10">
          <div className="grid md:grid-cols-2 gap-6 lg:grid-cols-3 xl:gap-8 ">
            <div className="border p-10 rounded-lg text-center">
              <span className="flex justify-center text-4xl text-emerald-500 mb-4 ">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  height="1em"
                  width="1em"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </span>
              <h5 className="text-xl mb-2 font-bold">{t("contact.emailUs", "Email Us")}</h5>
              <p className="mb-0 text-base opacity-90 leading-7">
                <a href={`mailto:${getEmailLink(isBn ? (settings.company_email_bn || "ccruidk@test.com") : (settings.company_email_en || "ccruidk@test.com"))}`} className="text-emerald-500 hover:text-emerald-600 transition-colors duration-200">
                  {isBn ? (settings.company_email_bn || "ccruidk@test.com") : (settings.company_email_en || "ccruidk@test.com")}{" "}
                </a>
                <br />
                {t("contact.emailDesc", "Interactively grow empowered for process-centric total linkage.")}
              </p>
            </div>
            <div className="border p-10 rounded-lg text-center">
              <span className="flex justify-center text-4xl text-emerald-500 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  height="1em"
                  width="1em"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </span>
              <h5 className="text-xl mb-2 font-bold">{t("contact.callUs", "Call Us")}</h5>
              <p className="mb-0 text-base opacity-90 leading-7">
                <a href={`tel:${settings.company_call_us_phone || "+012345-67900"}`} className="text-emerald-500 hover:text-emerald-600 transition-colors duration-200">
                  {isBn ? (settings.company_phone_bn || "ফোন: ০২.৩৫৬.১৬৬৬") : (settings.company_phone_en || "Tell: 02.356.1666")}{" "}
                </a>
                <br />
                {t("contact.callDesc", "Distinctively disseminate focused solutions clicks-and-mortar ministate.")}
              </p>
            </div>
            <div className="border p-10 rounded-lg text-center">
              <span className="flex justify-center text-4xl text-emerald-500 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  height="1em"
                  width="1em"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </span>
              <h5 className="text-xl mb-2 font-bold">{t("contact.location", "Location")}</h5>
              <p className="mb-0 text-base opacity-90 leading-7">
                <span className="hover:text-emerald-500 transition-colors duration-200 cursor-default">
                  {isBn ? (settings.company_address_bn || "৯৮৭ আন্দ্রে প্লেইন স্যুট, হাই স্ট্রিট ৮৩৮, লেক হেস্টারটাউন, ইউএসএ") : (settings.company_address_en || "987 Andre Plain Suite High Street 838, Lake Hestertown, USA")}
                </span>
                <br />
                {t("contact.locationDesc", "Cecilia Chapman, 561-4535 Nulla LA, United States 96522")}
              </p>
            </div>
          </div>
          <div className="px-0 pt-24 mx-auto items-center flex flex-col md:flex-row w-full justify-between">
            <div className="hidden md:w-full lg:w-5/12 lg:flex flex-col h-full">
              <span
                style={{
                  boxSizing: "border-box",
                  display: "inline-block",
                  overflow: "hidden",
                  width: "initial",
                  height: "initial",
                  background: "none",
                  opacity: "1",
                  border: "0px",
                  margin: "0px",
                  padding: "0px",
                  position: "relative",
                  maxWidth: "100%",
                }}
              >
                <span
                  style={{
                    boxSizing: "border-box",
                    display: "block",
                    width: "initial",
                    height: "initial",
                    background: "none",
                    opacity: "1",
                    border: "0px",
                    margin: "0px",
                    padding: "0px",
                    maxWidth: "100%",
                  }}
                >
                  <img
                    aria-hidden="true"
                    style={{
                      display: "block",
                      maxWidth: "100%",
                      width: "initial",
                      height: "initial",
                      background: "none",
                      opacity: "1",
                      border: "0px",
                      margin: "0px",
                      padding: "0px",
                    }}
                    src={contactUs}
                    alt="contact-us"
                  ></img>
                </span>
              </span>
            </div>
            <div className="px-0 pb-2 lg:w-5/12 flex flex-col md:flex-row">
              <form onSubmit={handleSubmit} className="w-full mx-auto flex flex-col justify-center">
                <div className="mb-12">
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-3">
                    {t("contact.supportTitle", "For any support just send your query")}
                  </h3>
                  <p className="text-base opacity-90 leading-7">
                    {t("contact.supportDesc", "Collaboratively promote client-focused convergence vis-a-vis customer directed alignments via plagiarize strategic users and standardized infrastructures.")}
                  </p>
                </div>
                {status && (
                  <div className={`p-4 rounded-md mb-6 text-sm ${status.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {status.message}
                  </div>
                )}
                <div className="flex flex-col space-y-5">
                  <div className="flex flex-col md:flex-row space-y-5 md:space-y-0 ">
                    <div className="w-full md:w-1/2">
                      <label className="block text-gray-500 font-medium text-sm leading-none mb-2">
                        {t("contact.formName", "Your Name")}
                      </label>
                      <div className="relative">
                        <input
                          name="name"
                          type="text"
                          required
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder={t("contact.formNamePlaceholder", "Enter Your Name")}
                          className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                        ></input>
                      </div>
                    </div>
                    <div className="w-full md:w-1/2 md:ml-2.5 lg:ml-5 mt-2 md:mt-0">
                      <label className="block text-gray-500 font-medium text-sm leading-none mb-2">
                        {t("contact.formEmail", "Your Email")}
                      </label>
                      <div className="relative">
                        <input
                          name="email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t("contact.formEmailPlaceholder", "Enter Your Email")}
                          className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                        ></input>
                      </div>
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      {t("contact.formSubject", "Subject")}
                    </label>
                    <div className="relative">
                      <input
                        name="subject"
                        type="text"
                        required
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={t("contact.formSubjectPlaceholder", "Enter Your Subject")}
                        className="py-2 px-4 md:px-5 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                      ></input>
                    </div>
                  </div>
                  <div className="relative mb-4">
                    <label className="block text-gray-500 font-medium text-sm leading-none mb-2">
                      {t("contact.formMessage", "Message")}
                    </label>
                    <textarea
                      name="message"
                      required
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="px-4 py-3 flex items-center w-full rounded appearance-none opacity-75 transition duration-300 ease-in-out text-sm focus:ring-0 bg-white border border-gray-300 focus:shadow-none focus:outline-none focus:border-gray-500 placeholder-body"
                      autoComplete="off"
                      spellCheck="false"
                      rows="4"
                      placeholder={t("contact.formMessagePlaceholder", "Write your message here")}
                    ></textarea>
                  </div>
                  <div className="relative">
                    <button
                      type="submit"
                      disabled={submitting}
                      data-variant="flat"
                      className="md:text-sm leading-4 inline-flex items-center cursor-pointer transition ease-in-out duration-300 font-semibold text-center justify-center border-0 border-transparent rounded-md placeholder-white focus-visible:outline-none focus:outline-none bg-emerald-500 text-white px-5 md:px-6 lg:px-8 py-3 md:py-3.5 lg:py-3 hover:text-white hover:bg-emerald-600 h-12 mt-1 text-sm lg:text-base w-full sm:w-auto disabled:opacity-50"
                    >
                      {submitting ? t("contact.formSubmitting", "Sending...") : t("contact.formSubmit", "Send Message")}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;
